"use client";

import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { parseListField } from "@/lib/utils/crm/list-field-utils";
import type { TagsFieldValue } from "@/lib/types/entity-page-types";

interface TagsFieldDisplayProps {
  value: TagsFieldValue;
  fieldId: string;
  maxItems?: number;
  className?: string;
}

/**
 * Display-only component for tags fields in table cells
 * Shows tags as badges in a compact format
 */
export function TagsFieldDisplay({
  value,
  fieldId,
  maxItems = 3,
  className = "",
}: TagsFieldDisplayProps) {
  const { tags, totalCount } = useMemo(() => {
    // Handle null, undefined, or empty string
    if (value === null || value === undefined || value === "") {
      return { tags: [], totalCount: 0 };
    }

    const parsed = parseListField(value);
    return {
      tags: parsed.slice(0, maxItems),
      totalCount: parsed.length,
    };
  }, [value, maxItems]);

  if (tags.length === 0) {
    return <div className={`text-xs text-muted-foreground italic ${className}`}>No tags</div>;
  }

  return (
    <div className={`flex max-w-full min-w-0 flex-wrap gap-1 overflow-hidden ${className}`}>
      {tags.map((tag) => (
        <Badge
          key={tag}
          variant="secondary"
          className="max-w-[140px] truncate text-xs"
          title={tag}
        >
          {tag}
        </Badge>
      ))}
      {totalCount > maxItems && (
        <Badge variant="outline" className="text-xs">
          +{totalCount - maxItems} more
        </Badge>
      )}
    </div>
  );
}
